import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Zap, Check } from "lucide-react";
import { toast } from 'sonner';
import { useUniversalTracking } from '@/hooks/useUniversalTracking';
import { UniversalSymptomTracker } from '@/components/mental/UniversalSymptomTracker';

interface PanicProtocolProps {
  userId: string;
  onBack: () => void;
}

type ExerciseStep = 'symptoms-before' | 'protocol' | 'symptoms-after' | 'complete';

export const PanicProtocol = ({ userId, onBack }: PanicProtocolProps) => {
  const [step, setStep] = useState<ExerciseStep>('symptoms-before');
  const [currentProtocolStep, setCurrentProtocolStep] = useState(0);
  const [startTime] = useState(new Date());
  const [symptomsBefore, setSymptomsBefore] = useState<Record<string, number>>({});
  const [symptomsAfter, setSymptomsAfter] = useState<Record<string, number>>({});
  const { logExercise, isLogging } = useUniversalTracking();

  const steps = [
    {
      title: "Recognise This is a Panic Attack",
      instruction: "Say to yourself: 'This is a panic attack. It feels terrible, but it's not dangerous. It will pass.'",
      duration: 30,
      rationale: "Panic attacks peak within 10 minutes and cannot harm you. Naming it reduces fear."
    },
    {
      title: "Ground Yourself in the Present",
      instruction: "Look around and name 5 things you can see, 4 you can touch, 3 you can hear, 2 you can smell, 1 you can taste.",
      duration: 60,
      rationale: "This engages your thinking brain and brings you out of pure panic response."
    },
    {
      title: "Slow Your Breathing",
      instruction: "Breathe in for 4 counts, hold for 4, out for 6. Repeat 5 times. Breathe through your nose if possible.",
      duration: 90,
      rationale: "Long exhales activate your calming nervous system (parasympathetic)."
    },
    {
      title: "Ride the Wave",
      instruction: "Notice the sensations without fighting them. Say: 'I can feel this, and I can survive this.'",
      duration: 60,
      rationale: "Fighting panic makes it stronger. Acceptance allows it to pass naturally."
    },
    {
      title: "Stay Where You Are",
      instruction: "If possible, don't leave the situation. Staying teaches your brain the situation is safe.",
      duration: 30,
      rationale: "Escaping reinforces the panic response. Staying breaks the avoidance cycle."
    }
  ];

  const progressPercent = ((currentProtocolStep + 1) / steps.length) * 100;

  const handleComplete = async () => {
    const durationMinutes = Math.round((new Date().getTime() - startTime.getTime()) / 60000);

    const success = await logExercise({
      exercise_type: 'panic_protocol',
      exercise_name: 'Panic Attack Protocol',
      category: 'anxiety',
      duration_minutes: durationMinutes,
      completion_data: {
        steps_completed: steps.length
      },
      symptoms_before: symptomsBefore,
      symptoms_after: symptomsAfter,
      xp_earned: 25
    });

    if (success) {
      toast.success('Protocol completed! Well done for riding through this.');
      setStep('complete');
    } else {
      toast.error('Could not save progress, but you still did brilliantly.');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <Button variant="outline" onClick={onBack} size="lg" className="text-base">
        ← Back to Anxiety Tools
      </Button>

      {step === 'symptoms-before' && (
        <div className="space-y-4">
          <Card className="bg-gradient-therapeutic border-primary/20">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-primary/10">
                  <Zap className="h-7 w-7 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Panic Attack Protocol</CardTitle>
                  <p className="text-base text-foreground/80 mt-1">
                    Step-by-step guidance through this moment
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-foreground/80 mb-4">
                First, let's quickly note how you're feeling right now. This helps track your progress.
              </p>
            </CardContent>
          </Card>
          <UniversalSymptomTracker
            mode="before"
            onRatingsChange={setSymptomsBefore}
            compact
          />
          <Button 
            onClick={() => setStep('protocol')} 
            className="w-full"
            size="lg"
            disabled={Object.keys(symptomsBefore).length === 0}
          >
            Start Protocol
          </Button>
        </div>
      )}

      {step === 'protocol' && (
        <Card className="bg-gradient-therapeutic border-primary/20">
          <CardHeader>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-primary/10">
                  <Zap className="h-7 w-7 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Panic Attack Protocol</CardTitle>
                  <p className="text-base text-foreground/80 mt-1">
                    Step-by-step guidance through this moment
                  </p>
                </div>
              </div>
              <Badge variant="secondary" className="text-base px-4 py-2">Step {currentProtocolStep + 1}/{steps.length}</Badge>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="bg-gradient-calm rounded-lg p-6 space-y-4">
              <h3 className="text-xl font-medium text-foreground">{steps[currentProtocolStep].title}</h3>
              <p className="text-base text-foreground leading-relaxed">
                {steps[currentProtocolStep].instruction}
              </p>
              <div className="bg-background/50 rounded-lg p-4 mt-4">
                <p className="text-sm text-foreground/80">
                  <span className="font-medium">Why this helps:</span> {steps[currentProtocolStep].rationale}
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              {currentProtocolStep > 0 && (
                <Button variant="outline" onClick={() => setCurrentProtocolStep(currentProtocolStep - 1)} size="lg" className="flex-1 text-base">
                  Previous Step
                </Button>
              )}
              {currentProtocolStep < steps.length - 1 ? (
                <Button onClick={() => setCurrentProtocolStep(currentProtocolStep + 1)} size="lg" className="flex-1 text-base">
                  Next Step
                </Button>
              ) : (
                <Button onClick={() => setStep('symptoms-after')} size="lg" className="flex-1 text-base">
                  Rate How You Feel Now
                </Button>
              )}
            </div>

            <div className="bg-primary/10 rounded-lg p-4 text-sm text-foreground">
              <p className="font-medium mb-2">Remember:</p>
              <p>Panic attacks are your body's false alarm system. They peak within 10 minutes and cannot harm you. 
              You've survived every panic attack you've ever had. You'll survive this one too.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 'symptoms-after' && (
        <div className="space-y-4">
          <UniversalSymptomTracker
            mode="after"
            previousRatings={symptomsBefore}
            onRatingsChange={setSymptomsAfter}
          />
          <Button 
            onClick={handleComplete} 
            className="w-full"
            size="lg"
            disabled={isLogging || Object.keys(symptomsAfter).length === 0}
          >
            <Check className="mr-2 h-5 w-5" />
            {isLogging ? 'Saving...' : 'Complete Protocol (+25 XP)'}
          </Button>
        </div>
      )}

      {step === 'complete' && (
        <Card className="bg-gradient-calm border-emerald-500/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
              <Check className="w-6 h-6" />
              Protocol Complete
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-foreground/80">
              You made it through. That took real courage. Remember, each time you ride through a panic attack 
              without fleeing, you teach your brain that it can cope.
            </p>
            <Button onClick={onBack} className="w-full" size="lg">
              Done
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};